/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ComponentProps,
  createContext,
  Dispatch,
  FunctionComponent,
  ReactNode,
  useContext,
  useReducer,
} from "react";

import { FC } from "react";
import { ModalProps } from "shared/components/modal/modal.types";

type UIState = {
  modal: {
    isOpened: boolean;
    component: FC | null;
    data: Partial<ModalProps> & Record<string, any>;
  };
};

type Action =
  | { type: "OPEN_MODAL"; component: FC; data: any }
  | { type: "CLOSE_MODAL" };

type UIContextType = UIState & {
  openModal: <T extends FunctionComponent<any>>(
    component: T,
    data?: ComponentProps<T> & ModalProps
  ) => void;
  closeModal: () => void;
};

const defaultUIValues: UIState = {
  modal: { isOpened: false, component: null, data: { title: "" } },
};

const UIContext = createContext<UIContextType | undefined>(undefined);

const UIReducer = (state: UIState, action: Action): UIState => {
  switch (action.type) {
    case "OPEN_MODAL":
      return {
        ...state,
        modal: {
          isOpened: true,
          component: action.component,
          data: action.data,
        },
      };
    case "CLOSE_MODAL":
      return {
        ...state,
        modal: { isOpened: false, component: null, data: {} },
      };
    default:
      return state;
  }
};

export const UIContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch]: [UIState, Dispatch<Action>] = useReducer(
    UIReducer,
    defaultUIValues
  );

  const openModal = <T extends FunctionComponent<any>>(
    component: T,
    data: ComponentProps<T> = {} as ComponentProps<T>
  ) => {
    dispatch({ type: "OPEN_MODAL", component, data });
  };

  const closeModal = () => {
    dispatch({ type: "CLOSE_MODAL" });
  };

  return (
    <UIContext.Provider
      value={{
        modal: state.modal,
        openModal,
        closeModal,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUIStore = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useUIContext must be used within UIProvider");
  }
  return context;
};
